import { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Search, User, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

// Default 4 category slugs — the mega-menu has bespoke entries for these, so we
// don't want them duplicated when we append dynamic categories from the API.
const DEFAULT_CATEGORY_SLUGS = new Set(["carpets", "rugs", "photo-frames", "kids-baskets"]);

function buildNav(extraCategories) {
  return [
    {
      label: "Shop Rugs",
      to: "/collections?cat=carpets",
      mega: {
        groups: [
          {
            title: "Shop Rugs",
            items: [
              { label: "View All Rugs", to: "/collections?cat=carpets" },
              { label: "Hand-Tufted Rugs", to: "/collections?cat=carpets&material=wool" },
              { label: "Runner Rugs", to: "/collections?cat=carpets&size=runner%20rugs" },
              { label: "Jute Rugs", to: "/collections?cat=rugs" },
              { label: "Chenille Rugs", to: "/collections?cat=carpets&material=chenille" },
              { label: "New Arrivals", to: "/collections?filter=new" },
              { label: "Bestsellers", to: "/collections?filter=bestsellers" },
              // Extra categories added by the admin appear here.
              ...extraCategories.map((c) => ({ label: c.label, to: `/collections?cat=${encodeURIComponent(c.slug)}` })),
            ],
          },
          {
            title: "Shop by Style",
            items: ["Modern", "Abstract", "Geometric", "Traditional", "Minimal", "Bohemian"].map((s) => ({
              label: s, to: `/collections?cat=carpets&style=${s.toLowerCase()}`,
            })),
          },
          {
            title: "Shop by Room",
            items: ["Living Room", "Bedroom", "Dining Area", "Entryway", "Kids' Room"].map((r) => ({
              label: r, to: `/collections?room=${encodeURIComponent(r.toLowerCase())}`,
            })),
          },
          {
            title: "Shop by Size",
            items: [
              { label: "Small Rugs", to: "/collections?size=small%20rugs" },
              { label: "Runner Rugs", to: "/collections?size=runner%20rugs" },
              { label: "4 × 6 ft", to: "/collections?size=4%20%C3%97%206%20ft" },
              { label: "5 × 7 ft", to: "/collections?size=5%20%C3%97%207%20ft" },
              { label: "5 × 8 ft", to: "/collections?size=5%20%C3%97%208%20ft" },
              { label: "Large Rugs", to: "/collections?size=large%20rugs" },
              { label: "Custom Size", to: "/custom" },
            ],
          },
        ],
        feature: {
          image: "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=900&q=80",
          label: "The Runner Edit",
          subline: "Long, narrow, quietly commanding.",
          to: "/collections?cat=carpets",
        },
      },
    },
    { label: "Custom Rugs", to: "/custom" },
    { label: "Wall Décor", to: "/collections?cat=photo-frames" },
    { label: "Bags & Baskets", to: "/collections?cat=kids-baskets" },
    { label: "Our Story", to: "/about" },
    { label: "Journal", to: "/journal" },
    { label: "Contact", to: "/contact" },
  ];
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null); // "Shop Rugs" | null
  const [extraCategories, setExtraCategories] = useState([]);
  const location = useLocation();
  const { count, setIsOpen } = useCart();

  // Fetch dynamic categories added by admin. Filter out the four defaults which
  // already have bespoke mega-menu entries above.
  useEffect(() => {
    const load = () => {
      api.get("/categories")
        .then((r) => setExtraCategories((r.data.categories || []).filter((c) => !DEFAULT_CATEGORY_SLUGS.has(c.slug))))
        .catch(() => {});
    };
    load();
    window.addEventListener("iryaa:categories-changed", load);
    return () => window.removeEventListener("iryaa:categories-changed", load);
  }, []);

  const NAV = useMemo(() => buildNav(extraCategories), [extraCategories]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setOpenMega(null); }, [location.pathname]);

  return (
    <header
      data-testid="v2-header"
      className={`sticky top-0 z-40 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#fbf9f5]/92 border-b border-[#e4ded2]" : "bg-[#fbf9f5] border-b border-transparent"
      }`}
      onMouseLeave={() => setOpenMega(null)}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {/* Left: mobile menu + logo */}
        <div className="flex items-center gap-4">
          <button
            data-testid="v2-mobile-toggle"
            className="lg:hidden text-[#1a1a1a]"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <Link to="/" data-testid="v2-logo-link" className="flex items-center gap-3.5 group">
            <Logo className="w-14 h-14 md:w-16 md:h-16 transition-transform duration-500 group-hover:rotate-[6deg]" />
            <div className="leading-none hidden sm:block">
              <div className="font-serif-warm text-[26px] md:text-[28px] tracking-tight text-[#1B3B36]">Iryaa Homes</div>
              <div className="overline-v2 text-[9px] mt-1 text-[#84968C]">handcrafted · india</div>
            </div>
          </Link>
        </div>

        {/* Center: desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.mega && setOpenMega(item.label)}
            >
              <NavLink
                to={item.to}
                end={item.to === "/"}
                data-testid={`v2-nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-1 text-[13px] tracking-wide font-medium transition-colors ${
                    isActive ? "text-[#b85742]" : "text-[#1a1a1a] hover:text-[#b85742]"
                  }`
                }
              >
                {item.label}
                {item.mega && <ChevronDown size={12} className="opacity-60" />}
              </NavLink>
            </div>
          ))}
        </nav>

        {/* Right: icons */}
        <div className="flex items-center gap-1 md:gap-2">
          <button className="hidden md:flex w-10 h-10 items-center justify-center text-[#1a1a1a] hover:text-[#b85742]" aria-label="Search" data-testid="v2-nav-search">
            <Search size={18} />
          </button>
          <button className="hidden md:flex w-10 h-10 items-center justify-center text-[#1a1a1a] hover:text-[#b85742]" aria-label="Account" data-testid="v2-nav-account">
            <User size={18} />
          </button>
          <button
            data-testid="v2-nav-cart"
            onClick={() => setIsOpen(true)}
            className="relative w-10 h-10 flex items-center justify-center text-[#1a1a1a] hover:text-[#b85742]"
            aria-label="Cart"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#b85742] text-[#fbf9f5] text-[10px] font-medium w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mega menu */}
      {openMega && NAV.find((n) => n.label === openMega)?.mega && (
        <div className="hidden lg:block border-t border-[#e4ded2] bg-[#fbf9f5] v2-mega">
          <div className="max-w-[1440px] mx-auto px-12 py-10 grid grid-cols-12 gap-10">
            {NAV.find((n) => n.label === openMega).mega.groups.map((g) => (
              <div key={g.title} className="col-span-2">
                <div className="overline-v2 text-[10px] mb-4">{g.title}</div>
                <ul className="space-y-2.5">
                  {g.items.map((it) => (
                    <li key={it.label}>
                      <Link to={it.to} className="text-[13px] text-[#4a4640] hover:text-[#b85742] v2-link">
                        {it.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="col-span-4">
              <Link to={NAV.find((n) => n.label === openMega).mega.feature.to} className="block group">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={NAV.find((n) => n.label === openMega).mega.feature.image} alt="" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <div className="mt-4">
                  <div className="overline-v2">Featured</div>
                  <div className="font-serif-warm text-2xl mt-1 text-[#1a1a1a]">{NAV.find((n) => n.label === openMega).mega.feature.label}</div>
                  <div className="text-[13px] text-[#4a4640] mt-1">{NAV.find((n) => n.label === openMega).mega.feature.subline}</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden ${mobileOpen ? "" : "pointer-events-none"}`}>
        <div
          className={`absolute inset-0 bg-[#1a1a1a]/50 transition-opacity ${mobileOpen ? "opacity-100" : "opacity-0"}`}
          onClick={() => setMobileOpen(false)}
        />
        <aside
          data-testid="v2-mobile-drawer"
          className={`absolute top-0 left-0 bottom-0 w-[86%] max-w-sm bg-[#fbf9f5] transform transition-transform duration-400 ease-out overflow-y-auto ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="flex justify-between items-center px-6 py-5 border-b border-[#e4ded2]">
            <div className="overline-v2">Menu</div>
            <button data-testid="v2-mobile-close" onClick={() => setMobileOpen(false)} className="text-[#1a1a1a]"><X size={20} /></button>
          </div>
          <nav className="px-6 py-6 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`v2-mobile-nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="block font-serif-warm text-2xl text-[#1a1a1a] py-3 border-b border-[#e4ded2] last:border-0"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </aside>
      </div>
    </header>
  );
}
